import useGroups from "providers/GroupsProvider/useGroups";
import { useFormikContext } from "formik";
import type { LeaderboardFormValues, LeaderboardTimePeriod } from "../LeaderboardEditable.types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { leaderboardTimePeriods } from "../LeaderboardEditable.config";
import { Group } from "contexts/GroupsContext/GroupsContext";
import Grid from "components/primitives/Grid";
import FormText from "components/Form/FormText";
import FormAutocomplete from "components/Form/FormAutocomplete/FormAutocomplete";
import Mui5 from "components/mui5";
import { Exercise, ExerciseMetric, Variants, ScoreMetadata, MetadataType } from "types";
import { Circuit, CircuitMetric } from "types/circuit";
import useExercises from "providers/ExercisesProvider/useExercises";
import useHyroxMetadata from "providers/HyroxMetadataProvider/useHyroxMetadata";
import useCanDo from "hooks/useCanDo";
import useGetAllCircuitsQuery from "hooks/useGetAllCircuitsQuery";
import { circuitsEnabled } from "config/features";
import CircuitHelpers from "helpers/CircuitHelpers";
import { exerciseAutocompleteFilterOptions, metadataAutocompleteFilterOptions } from "config/autocomplete";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from "components/Icons/SettingsIcon";
import CloseIcon from "@mui/icons-material/Close";
import * as Components from "./components";
import LeaderboardHelpers from "features/Leaderboards/helpers/LeaderboardHelpers";
import Tooltip from "components/Tooltip";
import PercentageRankingConfig from "./PercentageRankingConfig";
import LeaderboardFormHelpers from "features/Leaderboards/helpers/LeaderboardFormHelpers";
import CanDoAction from "components/CanDoAction";
import { useTranslation } from "react-i18next";
import AutocompleteOptionColourable from "components/AutocompleteOptionColourable";
import DeleteLeaderboardAction from "./components/DeleteLeaderboardAction";

interface LeaderboardEditableConfigPanelProps {
  configPanelOpen?: boolean;
  onCancelCreate(): void;
  isFullscreen?: boolean;
}

function LeaderboardEditableConfigPanel(props: LeaderboardEditableConfigPanelProps) {
  const { configPanelOpen = false } = props;
  const { t } = useTranslation();
  const [showConfig, setShowConfig] = useState(configPanelOpen);
  const groups = useGroups();
  const allExercises = useExercises();
  const hyroxMetadata = useHyroxMetadata();
  const canAccessHyrox = useCanDo("hyrox.any");
  const canAccessCircuits = useCanDo("library.any");
  const { data: circuits } = useGetAllCircuitsQuery({ enabled: circuitsEnabled && canAccessCircuits });

  const filterUnsortableMetrics = useCallback(
    (metrics?: ExerciseMetric[]) => metrics?.filter((metric) => metric.sortType !== null),
    []
  );
  const exercises = useMemo(
    () => allExercises.filter((exercise) => Boolean(filterUnsortableMetrics(exercise?.metrics)?.length)),
    [allExercises, filterUnsortableMetrics]
  );

  // Combine exercises, scores and circuits into metadata options
  type MetadataOption =
    | { type: MetadataType.EXERCISE; metadata: Exercise }
    | { type: MetadataType.SCORE; metadata: ScoreMetadata }
    | { type: MetadataType.CIRCUIT; metadata: Circuit };

  const metadataOptions = useMemo((): MetadataOption[] => {
    const exerciseOptions: MetadataOption[] = exercises.map((exercise) => ({
      type: MetadataType.EXERCISE,
      metadata: exercise,
    }));

    // Only include hyrox if feature is enabled
    const scoreOptions: MetadataOption[] = canAccessHyrox
      ? hyroxMetadata
          .filter((score) => score.id === "hyroxConditioning") // Only hyrox for now
          .map((score) => ({
            type: MetadataType.SCORE,
            metadata: score,
          }))
      : [];

    // Only scored circuits (at least one PB-enabled metric) can back a leaderboard
    const circuitOptions: MetadataOption[] = circuits
      .filter((circuit) => CircuitHelpers.getPbEnabledMetrics(circuit).length > 0)
      .map((circuit) => ({
        type: MetadataType.CIRCUIT,
        metadata: circuit,
      }));

    return [...exerciseOptions, ...scoreOptions, ...circuitOptions];
  }, [exercises, hyroxMetadata, canAccessHyrox, circuits]);

  const { values, setValues, touched, isValid, dirty, submitForm } = useFormikContext<LeaderboardFormValues>();

  useEffect(() => {
    const isComplete =
      values.measureType === "Score"
        ? Boolean(values.scoreMetadata)
        : values.measureType === "Circuit"
          ? Boolean(values.circuit && values.metric)
          : Boolean(values.exercise && values.metric);

    if (!(dirty && isValid && isComplete)) return;

    const timeout = setTimeout(() => {
      submitForm();
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [dirty, isValid, submitForm, values]);

  const unmountingRef = useRef(false);

  useEffect(
    () => () => {
      unmountingRef.current = true;
    },
    []
  );

  useEffect(() => {
    return () => {
      const isComplete =
        values.measureType === "Score"
          ? Boolean(values.scoreMetadata)
          : values.measureType === "Circuit"
            ? Boolean(values.circuit && values.metric)
            : Boolean(values.exercise && values.metric);

      if (!(unmountingRef.current && dirty && isValid && isComplete)) return;

      submitForm();
    };
  }, [dirty, isValid, submitForm, values]);

  const handleGroupsChange = useCallback(
    (_: any, groups: Group[]) => setValues((values) => ({ ...values, groups })),
    [setValues]
  );

  const handlePeriodChange = useCallback(
    (_: React.MouseEvent<HTMLElement, MouseEvent>, timePeriod: LeaderboardTimePeriod | null) => {
      /** if user clicks on the same time period it sends a null value */
      if (!timePeriod) return;
      return LeaderboardFormHelpers.onTimePeriodChange(setValues, timePeriod);
    },
    [setValues]
  );

  const { handleMetadataChange, handleMetricChange } = useMemo(() => {
    return {
      handleMetadataChange: (_: any, metadata: MetadataOption | null = null) => {
        setValues((prevValues) => {
          const defaultOldName = LeaderboardHelpers.getDefaultName(prevValues);

          if (!metadata) return prevValues;

          if (metadata.type === MetadataType.SCORE) {
            const { id, type, name, groups, comparePeriodType, startDateComparePeriod, rankType } = prevValues;
            return {
              id,
              type,
              name: defaultOldName && prevValues.name.indexOf(defaultOldName) !== -1 ? "" : name,
              groups,
              comparePeriodType,
              startDateComparePeriod,
              rankType,
              measureType: "Score",
              scoreMetadata: metadata.metadata,
            };
          }

          if (metadata.type === MetadataType.CIRCUIT) {
            const circuit = metadata.metadata;
            const pbMetrics = CircuitHelpers.getPbEnabledMetrics(circuit);
            const { id, type, name, groups, comparePeriodType, startDateComparePeriod, rankType } = prevValues;
            return {
              id,
              type,
              name: defaultOldName && prevValues.name.indexOf(defaultOldName) !== -1 ? "" : name,
              groups,
              comparePeriodType,
              startDateComparePeriod,
              rankType,
              measureType: "Circuit",
              circuit,
              metric: pbMetrics.find((metric) => metric.isDefault) || pbMetrics[0] || null,
            };
          }

          // Exercise type
          const exercise = metadata.metadata as Exercise;
          const { id, type, name, groups, comparePeriodType, startDateComparePeriod, rankType } = prevValues;
          const currentVariant = prevValues.measureType === "Exercise" ? prevValues.variant : null;
          return {
            id,
            type,
            name: defaultOldName && prevValues.name.indexOf(defaultOldName) !== -1 ? "" : name,
            groups,
            comparePeriodType,
            startDateComparePeriod,
            rankType,
            measureType: "Exercise",
            exercise,
            metric: exercise?.metrics.find((metric) => metric.isDefault) || exercise?.metrics[0] || null,
            variant: exercise?.variants.length ? currentVariant || exercise.variants[0] : null,
          };
        });
      },
      handleMetricChange: (_: any, metric: ExerciseMetric | CircuitMetric | null) => {
        setValues((prevValues) => {
          const defaultOldName = LeaderboardHelpers.getDefaultName(prevValues);

          return {
            ...prevValues,
            name: defaultOldName && prevValues.name.indexOf(defaultOldName) !== -1 ? "" : prevValues.name,
            metric,
          } as LeaderboardFormValues;
        });
      },
    };
  }, [setValues]);

  const handleVariantChange = useCallback(
    (_: any, variant: Variants | null = null) => {
      setValues((prevValues) => {
        const defaultOldName = LeaderboardHelpers.getDefaultName(prevValues);

        return {
          ...prevValues,
          name: defaultOldName && prevValues.name.indexOf(defaultOldName) !== -1 ? "" : prevValues.name,
          variant,
        };
      });
    },
    [setValues]
  );

  const toggleConfigPanel = useCallback(() => setShowConfig((prev) => !prev), []);

  const showVariantToggle = values.measureType === "Exercise" && Boolean(values.exercise?.variants.length);

  const metricOptions = useMemo<(ExerciseMetric | CircuitMetric)[]>(() => {
    if (values.measureType === "Exercise") {
      return filterUnsortableMetrics(values.exercise?.metrics) || [];
    }

    if (values.measureType === "Circuit") {
      return values.circuit ? CircuitHelpers.getPbEnabledMetrics(values.circuit) : [];
    }

    return [];
  }, [filterUnsortableMetrics, values]);

  return (
    <Grid
      container
      px={2}
      py={1.5}
      spacing={0}
      sx={{ width: "100%", flexDirection: "column", overflowY: "auto", overflowX: "hidden" }}
      columns={12}
      wrap="nowrap"
    >
      <Grid size={12}>
        <Collapse in={showConfig}>
          <Grid container>
            <Grid size={12} mt={1}>
              <Grid container columns={12} sx={{ alignItems: "center", marginBottom: "8px" }}>
                <Grid size="grow">
                  <FormText
                    name="name"
                    label={t("leaderboard.config_input_name_label")}
                    size="small"
                    margin="none"
                    placeholder={LeaderboardHelpers.getDefaultName(values)}
                    fullWidth
                  />
                </Grid>
                <Grid pl={2} mt={0.5} pr={0.5} height={32}>
                  <Divider orientation="vertical" />
                </Grid>
                <Grid>
                  <Tooltip title={t("global.close_label")} placement="top" arrow>
                    <IconButton
                      color="primary"
                      size="small"
                      aria-label={t("global.close_label")}
                      onClick={toggleConfigPanel}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container columns={12} spacing={0}>
                <Grid size={6} pr={0.5}>
                  <FormAutocomplete
                    name="metadata"
                    disableClearable={
                      !!(
                        (values.measureType === "Exercise" && values.exercise) ||
                        (values.measureType === "Score" && values.scoreMetadata) ||
                        (values.measureType === "Circuit" && values.circuit)
                      )
                    }
                    value={
                      values.measureType === "Score" && values.scoreMetadata
                        ? metadataOptions.find(
                            (opt) => opt.type === MetadataType.SCORE && opt.metadata.id === values.scoreMetadata?.id
                          )
                        : values.measureType === "Exercise"
                          ? metadataOptions.find(
                              (opt) =>
                                opt.type === MetadataType.EXERCISE &&
                                (opt.metadata as Exercise).id === values.exercise?.id
                            )
                          : values.measureType === "Circuit"
                            ? metadataOptions.find(
                                (opt) => opt.type === MetadataType.CIRCUIT && opt.metadata.id === values.circuit?.id
                              )
                            : undefined
                    }
                    options={metadataOptions}
                    renderInput={(params: any) => (
                      <Mui5.TextField
                        {...params}
                        margin="dense"
                        label={t("leaderboard.config_input_exercise_label")}
                        variant="outlined"
                        color="primary"
                        error={
                          values.measureType === "Exercise" &&
                          "exercise" in touched &&
                          touched.exercise !== undefined &&
                          !values.exercise
                        }
                        size="small"
                        fullWidth
                      />
                    )}
                    multiple={false}
                    groupBy={(option: MetadataOption) =>
                      option.type === MetadataType.EXERCISE
                        ? (option.metadata as Exercise).category
                        : option.type === MetadataType.CIRCUIT
                          ? t(`global.metadata_type_${MetadataType.CIRCUIT}`)
                          : t(`global.metadata_type_${MetadataType.SCORE}`)
                    }
                    getOptionLabel={(option: MetadataOption) => option.metadata.name}
                    onChange={handleMetadataChange}
                    disabled={!metadataOptions.length}
                    filterOptions={metadataAutocompleteFilterOptions}
                  />
                </Grid>
                {(values.measureType === "Exercise" || values.measureType === "Circuit") && (
                  <Grid size={6} pl={0.5}>
                    <Grid container columns={12} spacing={0}>
                      <Grid size={!showVariantToggle ? 12 : "grow"}>
                        <FormAutocomplete
                          name="metric"
                          disableClearable={!!values.metric}
                          value={values.metric}
                          options={metricOptions || []}
                          renderInput={(params: any) => (
                            <Mui5.TextField
                              {...params}
                              margin="dense"
                              label={t("leaderboard.config_input_metric_label")}
                              variant="outlined"
                              color="primary"
                              error={"metric" in touched && touched.metric && !values.metric}
                              size="small"
                              fullWidth
                            />
                          )}
                          multiple={false}
                          getOptionLabel={(option: ExerciseMetric | CircuitMetric) => option.name}
                          onChange={handleMetricChange}
                          disabled={!metricOptions?.length}
                          filterOptions={exerciseAutocompleteFilterOptions}
                        />
                      </Grid>
                      {values.measureType === "Exercise" && showVariantToggle && (
                        <Grid minWidth={"60px"}>
                          <Box sx={[{ textAlign: "center", pl: 1 }, Components.sxVariantToggleBox]}>
                            <Mui5.ToggleButtonGroup
                              value={values.variant}
                              onChange={handleVariantChange}
                              exclusive
                              size="small"
                              color="primary"
                              fullWidth
                              sx={{
                                height: props.isFullscreen ? "40px" : "37px",
                                mt: 0.5,
                              }}
                            >
                              <Mui5.ToggleButton
                                color="primary"
                                value="Left"
                                aria-label={t("measures.variant_aria_label_Left")}
                                size="small"
                                sx={{ px: 0.5 }}
                              >
                                {t("measures.variant_single_letter_Left")}
                              </Mui5.ToggleButton>
                              <Mui5.ToggleButton
                                color="primary"
                                value="Right"
                                aria-label={t("measures.variant_aria_label_Right")}
                                size="small"
                                sx={{ px: 0.5 }}
                              >
                                {t("measures.variant_single_letter_Right")}
                              </Mui5.ToggleButton>
                            </Mui5.ToggleButtonGroup>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid size={12} mb={0.5}>
              <Typography
                variant="caption"
                sx={[
                  { color: "rgba(0, 0, 0, 0.6)", fontWeight: "fontWeightRegular" },
                  Components.sxTimePeriodLabelTypography,
                ]}
              >
                {t("leaderboard.config_input_time_period_label")}
              </Typography>
              <Mui5.ToggleButtonGroup
                value={values.type}
                onChange={handlePeriodChange}
                exclusive
                size="small"
                fullWidth
                sx={{ height: props.isFullscreen ? "40px" : "37px" }}
              >
                {leaderboardTimePeriods.map((period) => (
                  <Mui5.ToggleButton
                    key={period.value}
                    value={period}
                    aria-label={`${t("leaderboard.config_input_time_period_label")}: ${t(period.label)}`}
                    size="small"
                    color="primary"
                    disabled={LeaderboardFormHelpers.isTimePeriodOptionDisabled(period, values)}
                  >
                    {t(period.label)}
                  </Mui5.ToggleButton>
                ))}
              </Mui5.ToggleButtonGroup>
            </Grid>
          </Grid>
          <Grid size={12}>
            <PercentageRankingConfig />
          </Grid>
        </Collapse>
      </Grid>
      <Grid size={12}>
        <Grid container alignItems="center">
          <Grid size="grow">
            <CanDoAction action="group.view">
              <FormAutocomplete
                multiple
                name="groups"
                value={values.groups}
                onChange={handleGroupsChange}
                getOptionLabel={(option) => option.name}
                options={groups}
                limitTags={2}
                renderOption={(optionProps, option) => (
                  <AutocompleteOptionColourable {...optionProps} key={option.id} colour={option.colour}>
                    {option.name}
                  </AutocompleteOptionColourable>
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Mui5.Chip key={option.id} label={option.name} customColor={option.colour} {...tagProps} />;
                  })
                }
                renderInput={(params) => (
                  <Mui5.TextField
                    {...params}
                    margin="dense"
                    label={t("leaderboard.config_input_groups_filter_label")}
                    variant="outlined"
                    color="primary"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </CanDoAction>
          </Grid>
          <Grid pl={2} mt={0.5} pr={0.5} height={32}>
            <Divider orientation="vertical" />
          </Grid>
          <Grid mt={0.5}>
            <DeleteLeaderboardAction onCancelCreate={props.onCancelCreate} />
          </Grid>
          <Grid mt={0.5}>
            <Tooltip title={t("global.configuration_label")} placement="top" arrow>
              <IconButton
                color="primary"
                size="small"
                onClick={toggleConfigPanel}
                aria-label={t("leaderboard.open_config_panel_leaderboard_aria_label", { name: values.name })}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default LeaderboardEditableConfigPanel;
